<template>
  <div class="border-b border-gray-200">
    <!-- Logo et bouton collapse -->
    <div class="flex items-center justify-between p-4">
      <div class="flex items-center" :class="{ 'justify-center': collapsed }">
        <img 
          src="/images/logo.png" 
          alt="RHCON360 Logo" 
          :class="collapsed ? 'hidden' : 'h-10'" 
          class="transition-all duration-300"
        />
      </div>
      <!-- Bouton collapse/expand (desktop seulement) -->
      <button 
        v-if="!collapsed"
        @click="$emit('toggle-collapse')"
        class="hidden lg:block p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
        </svg>
      </button>
      <button 
        v-else
        @click="$emit('toggle-collapse')"
        class="hidden lg:block p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
        </svg>
      </button>
    </div>
    
    <!-- Organisation -->
    <div v-if="organizationName && !collapsed" class="px-4 pb-3">
      <div class="flex items-center space-x-2.5 bg-gray-50 rounded-lg p-2.5 border border-gray-100">
        <div 
          v-if="organizationLogo" 
          class="h-7 w-7 rounded-md overflow-hidden bg-white border border-gray-200 flex-shrink-0 flex items-center justify-center shadow-sm"
        >
          <img 
            :src="organizationLogoUrl" 
            :alt="organizationName" 
            class="h-full w-full object-cover"
            @error="(e) => { (e.target as HTMLImageElement).style.display = 'none' }"
          />
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Organisation</p>
          <p class="text-sm font-semibold text-gray-900 truncate">{{ organizationName }}</p>
        </div>
      </div>
    </div>
    <!-- Organisation collapsed - juste le logo -->
    <div v-else-if="organizationLogo && collapsed" class="px-2 pb-3 flex justify-center">
      <div class="h-8 w-8 rounded-md overflow-hidden bg-white border border-gray-200 flex items-center justify-center shadow-sm">
        <img 
          :src="organizationLogoUrl" 
          :alt="organizationName || 'Logo'" 
          class="h-full w-full object-cover"
          @error="(e) => { (e.target as HTMLImageElement).style.display = 'none' }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { useOrganizationsStore } from '@/stores/organizations.store'

interface Props {
  collapsed: boolean
}

defineProps<Props>()

defineEmits<{
  'toggle-collapse': []
}>()

const authStore = useAuthStore()
const organizationsStore = useOrganizationsStore()

// Récupérer l'organisation de l'utilisateur
const userOrganization = computed(() => {
  const user = authStore.user
  if (!user?.organizationId) return null
  
  // Si organizationId est un objet (populate), retourner directement
  if (typeof user.organizationId === 'object' && user.organizationId !== null) {
    return user.organizationId
  }
  
  // Sinon, chercher dans le store des organisations
  const orgId = typeof user.organizationId === 'string' 
    ? user.organizationId 
    : user.organizationId._id || user.organizationId
    
  return organizationsStore.getOrganizationById(orgId) || null
})

const organizationName = computed(() => {
  if (!userOrganization.value) return null
  return userOrganization.value.name || null
})

const organizationLogo = computed(() => {
  if (!userOrganization.value) return null
  return userOrganization.value.logo || null
})

const organizationLogoUrl = computed(() => {
  if (!organizationLogo.value) return ''
  // Si le logo commence par /uploads, c'est un chemin relatif, on ajoute l'URL de l'API
  if (organizationLogo.value.startsWith('/uploads')) {
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    return `${apiBaseUrl}${organizationLogo.value}`
  }
  // Sinon, c'est déjà une URL complète
  return organizationLogo.value
})

// Charger les organisations si nécessaire
onMounted(async () => {
  const user = authStore.user
  if (organizationsStore.organizations.length === 0 && user?.organizationId) {
    try {
      await organizationsStore.fetchOrganizations()
    } catch (error) {
      console.error('Erreur lors du chargement des organisations:', error)
    }
  }
})
</script>
